/**
 * Chat orchestration hook (Task 201). Owns the chat lifecycle that App.tsx used
 * to concentrate: the streaming reducer, the SSE/non-stream answer runners, the
 * landing-shot morph state, session persistence, citation highlight, and every
 * submit/retry/stop/new-chat handler. Extracted verbatim from App.tsx so the
 * shell becomes a thin route switch; behavior is byte-for-byte unchanged.
 *
 * React discipline (docs/frontend-react-guidelines.md):
 *   - Submit is handler-driven (sendQuery awaits postAnswer, dispatches).
 *   - Display flags (loading, started, etc.) are render-derived.
 *   - Animation phases come from the reducer; the morph drives onPhaseComplete.
 *   - composer reset is via `key=`, not via prop-to-state mirroring.
 *   - The only Effect here is session persistence (an external store).
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { MorphGeometry, MorphDirection, MorphPhase } from "../components/MorphAnimation";
import {
  postAnswer,
  postAnswerStream,
  type AnswerCitation,
  type AnswerResponse,
} from "../api/answer-client";
import {
  activeQuery,
  chatReducer,
  getError,
  getThread,
  hasStarted,
  initialChatState,
  isLoading,
  pendingQuery,
  type ChatMessage,
  type ChatState,
} from "../state/chat-reducer";
import { clearSession, saveSession, sessionRevision } from "../state/session-persistence";
import { revealCitation } from "../utils/citation-anchors";
import { getStrings, type Language } from "../strings";
import { acknowledgementResponse, isAcknowledgement, ACKNOWLEDGE_ROUTE, CLARIFY_ROUTE } from "./acknowledgement";
import {
  MIN_DRAW_MS,
  nextPendingId,
  pickMorphDirection,
  readAnswerStreamUrl,
  readAnswerUrl,
  readFakeAnswerDelayMs,
  readFollowUpEnabled,
  readHoldToRelease,
  readStreamingEnabled,
} from "./answer-config";
import { buildRewriteContext, type AnswerExtras } from "./rewrite-context";
import type { ComposerPrefill } from "./use-composer-prefill";

type ChatStateKind =
  | "idle"
  | "drawing"
  | "landing"
  | "chat-active"
  | "errored";

/**
 * Latest assistant answer's sources for the desktop rail (Task 067), paired
 * with the user question that produced them (the rail subtitle). Derived from
 * the thread during render per docs/frontend-react-guidelines.md §5.2 — never
 * mirrored into state. Returns null until the first answer lands, so the rail
 * can render an empty shell that holds the column width without a layout jump.
 */
function latestAnswer(
  thread: ChatMessage[],
): { sources: AnswerCitation[]; query: string; id: string } | null {
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    const message = thread[i];
    if (message === undefined || message.role !== "assistant") continue;
    // Sources are known only after answer.final — skip a still-streaming turn so
    // the rail shows the previous answer's sources (or its empty shell) until the
    // current answer settles (Task 137: "render sources only after final").
    if (message.response === null) continue;
    // A courtesy reply or a request for the whole question cites nothing: the
    // rail keeps the last real answer's sources instead of going empty.
    const routeType = message.response.route.type;
    if (routeType === ACKNOWLEDGE_ROUTE || routeType === CLARIFY_ROUTE) continue;
    // The user turn immediately before carries the question for this answer.
    const prev = thread[i - 1];
    // A rewritten follow-up shows the question as it was understood.
    const rewritten = message.response.rewrite?.applied ? message.response.rewrite.standalone_query : undefined;
    const query = rewritten ?? (prev !== undefined && prev.role === "user" ? prev.text : "");
    return { sources: message.response.sources, query, id: message.id };
  }
  return null;
}

export interface UseChatOptions {
  persistedSession: { thread: ChatMessage[]; language: Language } | null;
  language: Language;
  reducedMotion: boolean;
  prefill: ComposerPrefill;
}

export interface UseChat {
  // Thread + derived flags.
  thread: ChatMessage[];
  pending: string | null;
  activeQ: string | null;
  loading: boolean;
  started: boolean;
  errored: boolean;
  banner: ReturnType<typeof getError>;
  latest: { sources: AnswerCitation[]; query: string; id: string } | null;
  citedSourceIndex: number | null;
  composerDisabled: boolean;
  // Idle-scene render flags.
  idleCharging: boolean;
  idleDim: boolean;
  idleLoadingReduced: boolean;
  // Morph overlay.
  showMorph: boolean;
  morphPhase: MorphPhase;
  morphDirection: MorphDirection;
  morphGeometry: MorphGeometry | null;
  // Imperative seams.
  composerRef: React.RefObject<HTMLFormElement>;
  holdToRelease: boolean;
  // Handlers.
  sendQuery: (query: string, opts?: { rewrite?: boolean }) => Promise<void>;
  handleStop: () => void;
  handleRetry: () => void;
  handleDismiss: () => void;
  handleNewChat: () => void;
  handleCite: (messageId: string, n: number) => void;
  handleUseExactWords: (originalQuery: string) => void;
  handlePhaseComplete: (completedPhase: Exclude<MorphPhase, "static">) => void;
  /** Reset the conversation for a reader "ask about this" (no abort, no prefill reset). */
  resetForAsk: () => void;
}

export interface FrameScheduler {
  request: (callback: FrameRequestCallback) => number;
  cancel: (handle: number) => void;
}

export interface DeltaFrameBuffer {
  push: (text: string) => void;
  flush: () => void;
  cancel: () => void;
}

/**
 * Coalesce provider-sized SSE deltas into at most one React commit per frame.
 * Terminal paths call flush() synchronously, so no text can be stranded behind
 * answer.final/error/stop. Kept as a pure factory so ordering and cancellation
 * are covered without mounting React in the hermetic streaming smoke.
 */
export function createDeltaFrameBuffer(
  commit: (text: string) => void,
  scheduler: FrameScheduler,
): DeltaFrameBuffer {
  let pending = "";
  let frame: number | null = null;
  let cancelled = false;

  const commitPending = (): void => {
    frame = null;
    if (cancelled || pending.length === 0) return;
    const text = pending;
    pending = "";
    commit(text);
  };

  return {
    push(text) {
      if (cancelled || text.length === 0) return;
      pending += text;
      if (frame === null) frame = scheduler.request(commitPending);
    },
    flush() {
      if (cancelled) return;
      if (frame !== null) {
        scheduler.cancel(frame);
        frame = null;
      }
      commitPending();
    },
    cancel() {
      cancelled = true;
      pending = "";
      if (frame !== null) scheduler.cancel(frame);
      frame = null;
    },
  };
}

/** Build the rewrite-only context identically for a submit and its retry. */
export function buildFollowUpExtras(
  thread: ChatMessage[],
  followUpEnabled: boolean,
): AnswerExtras {
  if (!followUpEnabled) return {};
  const context = buildRewriteContext(thread);
  return context.length > 0 ? { context } : {};
}

export function useChat(options: UseChatOptions): UseChat {
  const { persistedSession, language, reducedMotion, prefill } = options;
  const answerUrl = useMemo(readAnswerUrl, []);
  const streamUrl = useMemo(readAnswerStreamUrl, []);
  const streamingEnabled = useMemo(readStreamingEnabled, []);
  const followUpEnabled = useMemo(readFollowUpEnabled, []);
  const holdToRelease = useMemo(readHoldToRelease, []);

  const [state, dispatch] = useReducer(
    chatReducer,
    persistedSession,
    (restored): ChatState =>
      restored !== null && restored.thread.length > 0
        ? { kind: "chat-active", thread: restored.thread, pending: null }
        : initialChatState,
  );

  // Citation chip (Task 144): the index (0-based) of the last clicked source
  // within the LATEST answer, to border-highlight exactly that SourceRail card
  // (desktop). Index, not sourceKey — sibling passages split from one transcript
  // segment share a path+timestamp, so matching by key lit up several cards at
  // once (read as a duplicate). The chip carries its own number, so one chip ->
  // one card.
  const [citedSourceIndex, setCitedSourceIndex] = useState<number | null>(null);

  // Held in refs because they are imperative resources, not React state.
  const activeRequestRef = useRef<AbortController | null>(null);
  const activeDeltaFlushRef = useRef<(() => void) | null>(null);
  const composerRef = useRef<HTMLFormElement | null>(null);
  const [morphGeometry, setMorphGeometry] = useState<MorphGeometry | null>(null);
  const [morphDirection, setMorphDirection] = useState<MorphDirection>("bowstring");

  const runAnswerRequest = useCallback(
    async (pendingId: string, query: string, minDrawMs = 0, extras: AnswerExtras = {}): Promise<void> => {
      if (activeRequestRef.current !== null) {
        activeRequestRef.current.abort();
      }
      const controller = new AbortController();
      activeRequestRef.current = controller;

      // Wait for BOTH the answer and the loading-time floor (if any). The
      // arrow draw is the loading state; the floor only matters when the
      // response beats it. This is handler-side timing, not an Effect.
      // VITE_FAKE_ANSWER_DELAY_MS (test-only, Task 080) widens the floor so the
      // drawing/skeleton/pending states are observable in the visual smoke.
      const floorMs = Math.max(minDrawMs, readFakeAnswerDelayMs());
      const [result] = await Promise.all([
        postAnswer(answerUrl, query, {
          language,
          signal: controller.signal,
          ...(extras.context && extras.context.length > 0 ? { context: extras.context } : {}),
          ...(typeof extras.rewrite === "boolean" ? { rewrite: extras.rewrite } : {}),
          ...(extras.shareOrigin ? { shareOrigin: extras.shareOrigin } : {}),
        }),
        floorMs > 0
          ? new Promise<void>((resolve) => window.setTimeout(resolve, floorMs))
          : Promise.resolve(),
      ]);

      if (controller.signal.aborted) return;
      activeRequestRef.current = null;

      if (result.ok) {
        dispatch({
          type: "answer/received",
          pendingId,
          response: result.data,
          reducedMotion,
        });
      } else {
        dispatch({ type: "answer/error", pendingId, error: result.error });
      }
    },
    [answerUrl, language, reducedMotion],
  );

  const runAnswerStream = useCallback(
    async (pendingId: string, query: string, minDrawMs = 0, extras: AnswerExtras = {}): Promise<void> => {
      if (activeRequestRef.current !== null) {
        activeRequestRef.current.abort();
      }
      const controller = new AbortController();
      activeRequestRef.current = controller;

      // The arrow draw is the loading state until the FIRST token AND the draw
      // floor have both elapsed; only then does the arrow land and the chat open
      // (Task 074 timing preserved for streaming). Deltas arriving before that
      // are buffered and flushed on landing. VITE_FAKE_ANSWER_DELAY_MS (test-only)
      // widens the floor so the smoke can observe the drawing state.
      const floorMs = Math.max(minDrawMs, readFakeAnswerDelayMs());
      let landed = false;
      let floorDone = floorMs <= 0;
      let buffer = "";
      // A re-check announced before the turn opened is applied on landing.
      let rechecking = false;
      let finalResponse: AnswerResponse | null = null;
      let finalDispatched = false;

      const deltaBuffer = createDeltaFrameBuffer(
        (text) => {
          if (!controller.signal.aborted) {
            dispatch({ type: "answer/delta", pendingId, text });
          }
        },
        {
          request: (callback) => window.requestAnimationFrame(callback),
          cancel: (handle) => window.cancelAnimationFrame(handle),
        },
      );
      activeDeltaFlushRef.current = deltaBuffer.flush;

      const clearActive = (): void => {
        if (activeRequestRef.current === controller) {
          activeRequestRef.current = null;
          activeDeltaFlushRef.current = null;
        }
      };

      // Land (open the assistant turn) once the draw floor has elapsed — do NOT
      // wait for the first token. Real providers can take many seconds to their
      // first content token (measured ~9s for DeepSeek prefill); making the user
      // stare at a frozen idle scene that whole time reads as "broken". Instead
      // we open the chat right after the signature draw and show a "thinking"
      // state in the assistant turn until the first delta arrives. Any tokens
      // that DID arrive before the floor are buffered and flushed on landing
      // (so a fast/cached answer still plays the draw, then appears at once).
      // The final is dispatched exactly once, only after landing.
      const settle = (): void => {
        if (controller.signal.aborted) return;
        if (!landed && floorDone) {
          landed = true;
          dispatch({ type: "answer/stream-start", pendingId, reducedMotion });
          if (rechecking) dispatch({ type: "answer/rechecking", pendingId });
          if (buffer.length > 0) {
            deltaBuffer.push(buffer);
            buffer = "";
          }
        }
        if (landed && finalResponse !== null && !finalDispatched) {
          finalDispatched = true;
          deltaBuffer.flush();
          dispatch({ type: "answer/final", pendingId, response: finalResponse });
          deltaBuffer.cancel();
          clearActive();
        }
      };

      if (floorMs > 0) {
        window.setTimeout(() => {
          floorDone = true;
          settle();
        }, floorMs);
      } else {
        // No floor (follow-ups): open the thinking turn immediately on submit.
        settle();
      }

      await postAnswerStream(
        streamUrl,
        query,
        {
          onDelta: (text) => {
            if (controller.signal.aborted) return;
            if (landed) deltaBuffer.push(text);
            else buffer += text;
            settle();
          },
          onFinal: (response) => {
            if (controller.signal.aborted) return;
            finalResponse = response;
            settle();
          },
          onStatus: () => {
            if (controller.signal.aborted || rechecking) return;
            rechecking = true;
            if (landed) dispatch({ type: "answer/rechecking", pendingId });
          },
          onError: (error) => {
            if (controller.signal.aborted) return;
            // Errors do NOT wait for the draw floor. The reducer routes by state:
            // drawing/pending -> error shell; an already-streaming turn -> settled
            // incomplete in place (partial text kept).
            if (landed) deltaBuffer.flush();
            dispatch({ type: "answer/error", pendingId, error });
            deltaBuffer.cancel();
            clearActive();
          },
        },
        {
          language,
          signal: controller.signal,
          ...(extras.context && extras.context.length > 0 ? { context: extras.context } : {}),
          ...(typeof extras.rewrite === "boolean" ? { rewrite: extras.rewrite } : {}),
          ...(extras.shareOrigin ? { shareOrigin: extras.shareOrigin } : {}),
        },
      );
    },
    [language, reducedMotion, streamUrl],
  );

  // The active request runner: streaming by default, non-streaming fallback.
  const runAnswer = streamingEnabled ? runAnswerStream : runAnswerRequest;

  const sendQuery = useCallback(
    async (query: string, opts: { rewrite?: boolean } = {}): Promise<void> => {
      // Clear any citation highlight from the previous answer — the rail is about
      // to show a different answer's sources, so a stale index would mark a wrong card.
      setCitedSourceIndex(null);
      // Consume any composer prefill (reader-ask / shared link) and, for shared
      // links, learn whether the first send was as_is vs edited (Task 191/192/196).
      const shareOrigin = prefill.consumeShareOrigin(query);
      const pendingId = nextPendingId();
      const isFirstQuestion = state.kind === "idle" || state.kind === "errored";
      if (isFirstQuestion) {
        // First query plays the landing-shot. Pick a direction and capture the
        // (still-visible) idle composer's rect so the arrow draws over it.
        // MEASURE AFTER the submit dispatch + a paint (Task 224): a multi-line
        // prefill ("ask about this connection") collapses the composer to one
        // line when the text clears — measuring before that left the release
        // trace visibly larger than the box it surrounds.
        setMorphDirection(pickMorphDirection());
        dispatch({ type: "composer/submit", query, pendingId });
        if (composerRef.current !== null && !reducedMotion) {
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
          );
          setMorphGeometry(
            composerRef.current !== null
              ? { composerRect: composerRef.current.getBoundingClientRect() }
              : null,
          );
        } else {
          setMorphGeometry(null);
        }
        await runAnswer(pendingId, query, reducedMotion ? 0 : MIN_DRAW_MS, shareOrigin ? { shareOrigin } : {});
        return;
      }
      if (state.kind === "chat-active" && state.pending === null) {
        // Follow-up: no morph, no draw floor — the thread loader covers it.
        // Task 170: when follow-ups are enabled, send a small window of prior
        // settled turns so the server can rewrite an elliptical question into a
        // standalone one. `opts.rewrite === false` is the "usar mis palabras
        // exactas" path — suppress the rewrite and re-ask verbatim.
        if (followUpEnabled && isAcknowledgement(query)) {
          // A courtesy after an answer is answered here, with no request.
          dispatch({ type: "chat/send-next", query, pendingId });
          dispatch({
            type: "answer/received",
            pendingId,
            response: acknowledgementResponse(query, getStrings(language).acknowledgeReply),
            reducedMotion,
          });
          return;
        }
        const extras = buildFollowUpExtras(state.thread, followUpEnabled);
        if (typeof opts.rewrite === "boolean") extras.rewrite = opts.rewrite;
        if (shareOrigin) extras.shareOrigin = shareOrigin;
        dispatch({ type: "chat/send-next", query, pendingId });
        await runAnswer(pendingId, query, 0, extras);
        return;
      }
      // drawing / landing — ignore stray submits mid-sequence.
    },
    [followUpEnabled, language, prefill, reducedMotion, runAnswer, state],
  );

  // Task 170: "usar mis palabras exactas" — re-ask the same question with the
  // rewrite suppressed, so a wrong rewrite is one click to correct.
  const handleUseExactWords = useCallback(
    (originalQuery: string): void => {
      void sendQuery(originalQuery, { rewrite: false });
    },
    [sendQuery],
  );

  const handlePhaseComplete = useCallback(
    (completedPhase: Exclude<MorphPhase, "static">): void => {
      // Only the landing flight is time-boxed; the draw is held by the network.
      if (completedPhase === "landing" && state.kind === "landing") {
        dispatch({
          type: "motion/landing-complete",
          pendingId: state.pendingId,
        });
      }
    },
    [state],
  );

  const handleRetry = useCallback((): void => {
    if (state.kind !== "errored") return;
    const last = state.lastQuery;
    if (last.length === 0) return;
    const hadThread = state.thread.length > 0;
    const pendingId = nextPendingId();
    dispatch({ type: "error/dismiss" });
    if (hadThread) {
      // Follow-up retry: back to chat-active, no morph.
      const extras = buildFollowUpExtras(state.thread, followUpEnabled);
      dispatch({ type: "chat/send-next", query: last, pendingId });
      void runAnswer(pendingId, last, 0, extras);
      return;
    }
    // First-query retry replays the landing-shot.
    setMorphDirection(pickMorphDirection());
    if (composerRef.current !== null && !reducedMotion) {
      setMorphGeometry({ composerRect: composerRef.current.getBoundingClientRect() });
    } else {
      setMorphGeometry(null);
    }
    dispatch({ type: "composer/submit", query: last, pendingId });
    void runAnswer(pendingId, last, reducedMotion ? 0 : MIN_DRAW_MS);
  }, [followUpEnabled, reducedMotion, runAnswer, state]);

  const handleDismiss = useCallback((): void => {
    dispatch({ type: "error/dismiss" });
  }, []);

  const handleStop = useCallback((): void => {
    // Visible dock stop control: abort the in-flight request FIRST (so the stream
    // callbacks early-return on the aborted signal and no further deltas land),
    // then settle the turn as "stopped" keeping whatever partial text arrived.
    // Handler-driven, not an Effect (frontend-react-guidelines §4.1).
    if (activeRequestRef.current !== null) {
      // Commit the last sub-frame batch before aborting; otherwise a click in
      // the same frame as a delta could visibly lose those final words.
      activeDeltaFlushRef.current?.();
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
      activeDeltaFlushRef.current = null;
    }
    dispatch({ type: "answer/stop" });
  }, []);

  const handleNewChat = useCallback((): void => {
    // New conversation (Task 086): abort any in-flight request, then reset the
    // reducer to idle. NO page reload — language selection and URL params are
    // preserved, and the composer remounts cleanly via its key (active -> idle).
    // Handler-driven, not an Effect.
    if (activeRequestRef.current !== null) {
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
    }
    // Task 167: clearing the conversation also drops the persisted session, so a
    // reload after "Nueva conversación" starts fresh. Done explicitly here (not
    // only via the persist Effect) so the clear is immediate and unambiguous.
    clearSession();
    dispatch({ type: "chat/reset" });
    prefill.reset(); // Task 191/192/196: a new chat is not a reader/shared origin
  }, [prefill]);

  const resetForAsk = useCallback((): void => {
    // Reader "ask about this" (Task 191): clear the persisted/active conversation
    // so the freshly-mounted idle composer starts the reader prompt as an
    // unambiguous standalone. No abort (we're navigating from the reader, not
    // mid-stream) and no prefill reset (the caller seeds the draft next).
    clearSession();
    dispatch({ type: "chat/reset" });
  }, []);

  // Render-derived booleans. NOT useState, NOT useEffect.
  const loading = isLoading(state);
  const started = hasStarted(state);
  const thread = getThread(state);
  const errored = state.kind === "errored";
  const banner = getError(state);

  const persistedRevisionRef = useRef<string | null>(null);

  // external: window.sessionStorage — persist the conversation so a reload in the
  // same tab restores it (Task 167). The Effect still observes reducer state, but
  // the cheap settled-history revision prevents JSON serialization/storage writes
  // for streaming deltas. It runs only when a turn settles or language changes.
  useEffect(() => {
    const revision = `${language}:${sessionRevision(thread)}`;
    if (revision === persistedRevisionRef.current) return;
    persistedRevisionRef.current = revision;
    saveSession(thread, language);
  }, [thread, language]);

  // The arrow morph plays over the still-visible idle scene during drawing +
  // landing (Task 074). Skipped entirely under reduced motion.
  const showMorph =
    (state.kind === "drawing" || state.kind === "landing") && !reducedMotion;
  const morphPhase: MorphPhase =
    state.kind === "drawing"
      ? "drawing"
      : state.kind === "landing"
        ? "landing"
        : "static";
  const pending = pendingQuery(state);
  // The question currently being answered (pending OR streaming) — drives the
  // SourceRail's loading/withholding state for the full stream (Task 137).
  const activeQ = activeQuery(state);
  const composerDisabled = loading;
  // Idle scene transitions (computed during render):
  //  - drawing:  scene stays VISIBLE; the trace draws over the live composer,
  //    while the hero text recedes to focus the charging composer.
  //  - landing:  scene fades out as the arrow lands and the chat reveals.
  const idleCharging = state.kind === "drawing";
  const idleDim = state.kind === "landing";
  // Reduced motion plays no arrow, so the idle scene shows a plain loading hint
  // while the first answer is in flight.
  const idleLoadingReduced = state.kind === "drawing" && reducedMotion;
  // Latest answer's sources for the desktop rail (Task 067). Render-derived;
  // null until the first answer lands.
  const latest = latestAnswer(thread);

  // Citation-chip click (Task 144): on desktop the rail shows the LATEST answer's
  // sources in order, so a chip in THAT answer maps 1:1 to the nth rail card —
  // highlight exactly that card by index (n-1). Matching by sourceKey instead lit
  // up every card sharing the same path+timestamp (sibling passages split from one
  // transcript segment), which reads as a duplicate. A chip in an OLDER answer (or
  // any chip on mobile, where the rail is hidden) reveals that answer's own inline
  // sources. Handler-driven, not an Effect.
  const handleCite = useCallback(
    (messageId: string, n: number): void => {
      const desktop =
        typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
      if (desktop && latest !== null && messageId === latest.id) {
        setCitedSourceIndex(n - 1);
      } else {
        revealCitation(messageId, n);
      }
    },
    [latest],
  );

  // Type-level exhaustiveness check on ChatState kinds.
  const exhaustivenessCheck: ChatStateKind = state.kind;
  void exhaustivenessCheck;

  return {
    thread,
    pending,
    activeQ,
    loading,
    started,
    errored,
    banner,
    latest,
    citedSourceIndex,
    composerDisabled,
    idleCharging,
    idleDim,
    idleLoadingReduced,
    showMorph,
    morphPhase,
    morphDirection,
    morphGeometry,
    composerRef,
    holdToRelease,
    sendQuery,
    handleStop,
    handleRetry,
    handleDismiss,
    handleNewChat,
    handleCite,
    handleUseExactWords,
    handlePhaseComplete,
    resetForAsk,
  };
}
